/**
 * Core Layer - Token Utilities
 * Pure functions for JWT token inspection (decode-only, no verification).
 * Server-side verification is the source of truth — these utilities are
 * used client-side to avoid rendering protected UIs with stale tokens.
 */

/**
 * Minimal JWT payload shape (only the fields we inspect).
 */
interface JwtPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

/**
 * Safely decode the payload section of a JWT (the middle Base64-URL segment).
 * Returns `null` if the token is malformed or not a valid JWT.
 *
 * NOTE: This does NOT verify the signature — that's the server's job.
 */
export const getTokenPayload = (token: string): JwtPayload | null => {
  try {
    // Strip "Bearer " prefix if present
    const raw = token.startsWith("Bearer ") ? token.slice(7) : token;

    const parts = raw.split(".");
    if (parts.length !== 3) return null;

    // Base64-URL → Base64 → decode
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join("")
    );

    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
};

/**
 * Check whether a JWT token is expired.
 *
 * @param token       - The raw JWT string (with or without "Bearer " prefix)
 * @param bufferSeconds - Grace period in seconds to account for clock skew
 *                        between client and server. Defaults to 60 s.
 * @returns `true` if the token is expired, malformed, or missing an `exp` claim.
 */
export const isTokenExpired = (
  token: string,
  bufferSeconds: number = 60
): boolean => {
  const payload = getTokenPayload(token);
  if (!payload?.exp) return true; // No exp → treat as expired

  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= payload.exp - bufferSeconds;
};
